// ─── Routes ──────────────────────────────────────────────────────────────
export const PORTAL_ROUTES = {
  ROOT: '/portal',
  DASHBOARD: '/portal/dashboard',
  APPOINTMENTS: '/portal/appointments',
  CONVERSATIONS: '/portal/conversations',
} as const;

// ─── Resource types ──────────────────────────────────────────────────────
// These appear as both URL segments under /:resourceType/:encodedResourceId
// and as `resource_type` values sent to the backend during DOB auth.
export const PORTAL_RESOURCE_TYPES = {
  APPOINTMENTS: 'appointments',
  CONVERSATIONS: 'conversations',
} as const;

export type PortalResourceType =
  typeof PORTAL_RESOURCE_TYPES[keyof typeof PORTAL_RESOURCE_TYPES];

// ─── Appointment status (raw backend values) ─────────────────────────────
export const APPOINTMENT_STATUS = {
  SCHEDULED: 'scheduled',
  CANCELLED: 'cancelled',
  COMPLETED: 'completed',
  UNSCHEDULED: 'unscheduled',
} as const;

export type AppointmentStatus =
  typeof APPOINTMENT_STATUS[keyof typeof APPOINTMENT_STATUS];

// ─── Branding / UI ───────────────────────────────────────────────────────
// SyncLoader takes a hex; mirrors `teamcare-blue` in tailwind.config.js.
export const BRAND_LOADER_COLOR = '#4A90E2';

// ─── Timing ──────────────────────────────────────────────────────────────
// How often the "Next Appointment" countdown re-renders. One minute is
// enough precision for "in 3h 12m"-style display.
export const COUNTDOWN_TICK_MS = 60_000;

// ─── External services ───────────────────────────────────────────────────
export const EXTERNAL_URLS = {
  GOOGLE_CALENDAR_RENDER: 'https://calendar.google.com/calendar/render',
  GOOGLE_MAPS_SEARCH: 'https://www.google.com/maps/search/',
} as const;

// ─── Long content strings ────────────────────────────────────────────────
interface ContentNote {
  title: string;
  description: string;
}

// Preparation notes are not yet delivered by the backend. Hardcoded for now.
export const PREPARATION_NOTES: ContentNote[] = [
  {
    title: 'Arrive early',
    description: 'Please arrive 15 minutes before your scheduled time to complete paperwork.',
  },
  {
    title: 'Bring reports',
    description: 'Carry your previous dental history or any relevant medical reports.',
  },
  {
    title: 'Avoid eating',
    description: 'Do not consume heavy meals or sticky foods 2 hours prior to your visit.',
  },
  {
    title: 'Inform about medications',
    description: 'Update our staff on any current medications you are taking.',
  },
];

// After-care instructions shown once the visit is completed. Generic
// oral-hygiene guidance — not specific to the procedure performed.
export const AFTER_CARE_NOTES: ContentNote[] = [
  {
    title: 'Brush gently',
    description: 'Brush twice a day with a soft-bristled brush; avoid the treated area for the first 24 hours.',
  },
  {
    title: 'Floss daily',
    description: 'Floss carefully once a day to keep gums healthy and prevent plaque buildup.',
  },
  {
    title: 'Eat soft foods',
    description: 'Stick to soft, lukewarm foods for the rest of the day and avoid hard or sticky items.',
  },
  {
    title: 'Stay hydrated',
    description: 'Drink plenty of water and limit sugary drinks to support healing and oral health.',
  },
];
